import React, { useContext, useState } from 'react'
import api from '../../api-client'
import { toast } from 'react-toastify'
import {
  IChecklistItem,
  ChecklistItemType,
  ChecklistItemSubType,
  ChecklistPhase,
  ChecklistOwner,
} from '@oneethos/shared'
import { AppContext } from '../../appContext'
import actions from '../../actions/checklistItemActions'
import { selectAllChecklistItems } from '../../selectors/checklistItems'
import ChecklistItemsList from './checklist-items-list'

export const ChecklistItemsConfig = () => {
  const [isAdding, setIsAdding] = useState(false)
  const [editingItem, setEditingItem] = useState<IChecklistItem | null>(null)
  const [newItem, setNewItem] = useState<Partial<IChecklistItem>>({})

  const { state, dispatch } = useContext(AppContext)
  const checklistItems = selectAllChecklistItems(state)
  const saving = state.checklistItemsConfig.saving

  const onSave = async () => {
    try {
      dispatch(actions.saveChecklistItemRequest())
      const saved = await api.post('/checklist-items', newItem)
      dispatch(actions.saveChecklistItemSuccess(saved))
      toast.success('Checklist item created successfully')
      cancelEditing()
    } catch (error: any) {
      dispatch(actions.saveChecklistItemError(error?.error || 'Failed to save'))
      toast.error('Failed to save checklist item')
    }
  }

  const onEdit = async () => {
    try {
      dispatch(actions.saveChecklistItemRequest())
      const updated = await api.put(`/checklist-items/${editingItem.id}`, newItem)
      dispatch(actions.saveChecklistItemSuccess(updated))
      toast.success('Checklist item updated successfully')
      cancelEditing()
    } catch (error: any) {
      dispatch(actions.saveChecklistItemError(error?.error || 'Failed to update'))
      toast.error('Failed to update checklist item')
    }
  }

  const startAdding = () => {
    setEditingItem(null)
    setNewItem({
      label: '',
      description: '',
      type: ChecklistItemType.System,
      subType: ChecklistItemSubType.Internal,
      phase: ChecklistPhase.LoanApplication,
      owner: ChecklistOwner.Installer,
      dataField: '',
      active: true,
      fundReleaseTypes: [],
      fundingSchedules: [],
      agentWorkflowId: null,
      agentWorkflowEnabled: false,
      presubmitAgentWorkflowId: null,
      sequence: null,
    })
    setIsAdding(true)
  }

  const startEditing = (item: IChecklistItem) => {
    setIsAdding(false)
    setEditingItem(item)
    setNewItem({
      label: item.label,
      description: item.description,
      type: item.type,
      subType: item.subType,
      phase: item.phase,
      owner: item.owner,
      dataField: item.dataField,
      active: item.active,
      fundReleaseTypes: item.fundReleaseTypes || [],
      fundingSchedules: item.fundingSchedules || [],
      agentWorkflowId: item.agentWorkflowId,
      agentWorkflowEnabled: item.agentWorkflowEnabled,
      presubmitAgentWorkflowId: item.presubmitAgentWorkflowId,
      sequence: item.sequence,
    })
  }

  const toggleItem = (item: IChecklistItem) => {
    if (editingItem?.id === item.id) {
      cancelEditing()
      return
    }

    startEditing(item)
  }

  const cancelEditing = () => {
    setIsAdding(false)
    setEditingItem(null)
    setNewItem({})
  }

  return (
    <ChecklistItemsList
      checklistItems={checklistItems}
      editingItem={editingItem}
      isAdding={isAdding}
      newItem={newItem}
      setNewItem={setNewItem}
      saving={saving}
      onToggleItem={toggleItem}
      onStartAdding={startAdding}
      onSave={onSave}
      onEdit={onEdit}
      onCancel={cancelEditing}
    />
  )
}
